import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { P, useAuth } from './auth';

/**
 * Whether the signed-in person decides break requests: an administrator
 * (`attendance.breaks.approve`), or someone picked on a break type to approve it
 * (operator, 2026-10-19), which `/attendance/me` says — the query the top bar
 * keeps fresh, so being picked shows without signing in again.
 */
export function useBreakApprover(): boolean {
  const { can } = useAuth();
  const { data } = useQuery({
    queryKey: ['attendance', 'me'],
    queryFn: () => api.get<{ approvesBreakRequests?: boolean }>('/attendance/me'),
    staleTime: 15_000,
  });
  return can(P.ATTENDANCE_BREAKS_APPROVE) || !!data?.approvesBreakRequests;
}

/**
 * How many break requests wait for the signed-in approver: the sidebar's count
 * beside Attendance and the Break requests tab's — one query. Nothing is asked
 * of anyone else.
 */
export function useWaitingBreakRequests(): number {
  const approver = useBreakApprover();
  const { data } = useQuery({
    queryKey: ['attendance', 'break-requests', 'count'],
    queryFn: () => api.get<{ count: number }>('/attendance/break-requests/count'),
    enabled: approver,
    // A new one rings the bell too, so this keeps the bell's pace.
    refetchInterval: 30_000,
  });
  return approver ? (data?.count ?? 0) : 0;
}
